/**
 * Hook for managing the Dhyana page onboarding tour using Driver.js
 * Auto-triggers on first visit, can be replayed via Help button
 */

import { useCallback, useEffect, useRef } from 'react';
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const TOUR_COMPLETED_KEY = 'dhyana_tour_completed';

interface UseOnboardingTourOptions {
  isReady: boolean; // Only start tour after PDF loads
  onSwitchToThoughtPad?: () => void; // Callback to switch to ThoughtPad tab
  onOpenSidebar?: () => void; // Callback to open sidebar if closed
  onSwitchToChat?: () => void; // Callback to switch to Chat tab
}

interface UseOnboardingTourReturn {
  startTour: () => void;
  hasCompletedTour: boolean;
}

interface TourCallbacks {
  onSwitchToThoughtPad?: () => void;
  onOpenSidebar?: () => void;
  onSwitchToChat?: () => void;
}

// Tour step definitions - will be generated dynamically to include callbacks
const createTourSteps = (callbacks: TourCallbacks) => [
  {
    popover: {
      title: 'First time here? Let\'s explore!',
      description: 'Quick 30-second tour. Trust us, it\'s worth it.',
      side: 'over' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-pdf-nav',
    popover: {
      title: 'Flip through pages',
      description: 'Arrow buttons, keyboard shortcuts (← →), or just type the page number. Whatever feels right.',
      side: 'top' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-zoom',
    popover: {
      title: 'Zoom in, zoom out',
      description: 'Tiny fonts? We got you. Cmd/Ctrl + or - works too.',
      side: 'top' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-contents',
    popover: {
      title: 'Skip the scrolling',
      description: 'Jump straight to any chapter or section. No endless scrolling.',
      side: 'top' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-bookmarks',
    popover: {
      title: 'Save for later',
      description: 'Found something important? Bookmark it. Color-code it. Never lose it again.',
      side: 'top' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-mnemo',
    popover: {
      title: 'Mnemo — Your Memory, Managed',
      description: 'Quick comprehension check on what you just read. Research shows self-testing boosts retention by 50%. Try it!',
      side: 'bottom' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-tldr',
    popover: {
      title: 'TL;DR Summary',
      description: 'One click = key concepts, examples, and memory tricks for the current page. Try Cmd/Ctrl + T!',
      side: 'bottom' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-sidebar',
    popover: {
      title: 'Your command center',
      description: 'Chat for answers, ThoughtPad for notes. Everything you need, one sidebar away.',
      side: 'left' as const,
      align: 'center' as const,
    },
    onHighlightStarted: () => {
      callbacks.onOpenSidebar?.();
    }
  },
  {
    element: '#tour-chat-tab',
    popover: {
      title: 'Stuck on something?',
      description: 'Ask away! Upload images, highlight confusing sections, or just type your question.',
      side: 'bottom' as const,
      align: 'center' as const,
    },
    onHighlightStarted: () => {
      callbacks.onSwitchToChat?.();
    }
  },
  {
    element: '#tour-text-highlight',
    popover: {
      title: 'Pro tip: Select any text',
      description: 'Highlight text on the page and it auto-magically lands in your chat. No copy-paste needed.',
      side: 'left' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-thoughtpad-tab',
    popover: {
      title: 'ThoughtPad',
      description: 'All your highlights, notes, and saved answers live here. Your personal study vault.',
      side: 'bottom' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-attachments',
    popover: {
      title: 'Attach images',
      description: 'Got a diagram from somewhere else? Upload it and ask questions about it.',
      side: 'top' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-snip',
    popover: {
      title: 'Snip from the page',
      description: 'See a tricky diagram? Snip it, ask about it. Works like magic.',
      side: 'top' as const,
      align: 'center' as const,
    }
  },
  {
    element: '#tour-help',
    popover: {
      title: 'That\'s it!',
      description: 'Click here anytime to replay this tour. Now go ace your study session!',
      side: 'bottom' as const,
      align: 'center' as const,
    }
  }
];

export function useOnboardingTour({ isReady, onSwitchToThoughtPad, onOpenSidebar, onSwitchToChat }: UseOnboardingTourOptions): UseOnboardingTourReturn {
  const driverRef = useRef<Driver | null>(null);
  const hasTriggeredRef = useRef(false);

  // Check if tour has been completed
  const hasCompletedTour = typeof window !== 'undefined'
    ? localStorage.getItem(TOUR_COMPLETED_KEY) === 'true'
    : false;

  // Mark tour as completed
  const markTourComplete = useCallback(() => {
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
  }, []);

  // Start the tour
  const startTour = useCallback(() => {
    // Clean up any existing driver instance
    if (driverRef.current) {
      driverRef.current.destroy();
    }

    // Ensure sidebar is visible before tour starts (for #tour-sidebar step)
    onOpenSidebar?.();

    const tourSteps = createTourSteps({
      onSwitchToThoughtPad,
      onOpenSidebar,
      onSwitchToChat
    });

    // Temporarily disable backdrop-filter on sidebar to fix stacking context issue
    // backdrop-filter creates a new stacking context which prevents Driver.js from highlighting elements inside
    const sidebar = document.getElementById('tour-sidebar');
    if (sidebar) {
      sidebar.style.setProperty('backdrop-filter', 'none');
      sidebar.style.setProperty('-webkit-backdrop-filter', 'none');
    }

    driverRef.current = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: tourSteps,
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Let\'s go!',
      progressText: '{{current}} of {{total}}',
      onDestroyStarted: () => {
        // Restore backdrop-filter on sidebar when tour ends
        const sidebar = document.getElementById('tour-sidebar');
        if (sidebar) {
          sidebar.style.removeProperty('backdrop-filter');
          sidebar.style.removeProperty('-webkit-backdrop-filter');
        }
        // Mark as complete when user finishes or skips
        markTourComplete();
        driverRef.current?.destroy();
      },
      onDestroyed: () => {
        driverRef.current = null;
      }
    });

    // Delay to ensure sidebar renders and DOM elements are ready
    setTimeout(() => {
      driverRef.current?.drive();
    }, 600);
  }, [markTourComplete, onSwitchToThoughtPad, onOpenSidebar, onSwitchToChat]);

  // Auto-trigger tour on first visit
  useEffect(() => {
    if (isReady && !hasCompletedTour && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      // Delay to let the page fully render
      const timer = setTimeout(() => {
        startTour();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isReady, hasCompletedTour, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  return {
    startTour,
    hasCompletedTour
  };
}
